/**
 * Email Signature Generator Utilities
 * Supports:
 * 1. Email-safe Compact HTML Table Business Card (Bulletproof layout for all email clients)
 * 2. Retina 2x Canvas PNG Business Card Image (Fixed visual rendering)
 */

export const DEFAULT_CARD_HTML_TEMPLATE = `<table cellpadding="0" cellspacing="0" border="0" style="width: 308px; max-width: 100%; border: 1px solid #e2e8f0; border-radius: 12px; background-color: #ffffff; font-family: -apple-system, BlinkMacSystemFont, 'Pretendard', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; box-shadow: 0 1px 3px rgba(0,0,0,0.05); margin-top: 14px; margin-bottom: 6px; border-collapse: separate; mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
  <tr>
    <td style="padding: 12px 14px; border-left: 4px solid #4f46e5; border-radius: 12px 0 0 12px;">
      <!-- Header / Logo & Badge -->
      <table cellpadding="0" cellspacing="0" border="0" style="width: 100%; margin-bottom: 6px;">
        <tr>
          <td style="vertical-align: middle;">
            <span style="font-size: 14px; font-weight: 900; color: #0f172a; letter-spacing: -0.5px;">Clouflake</span>
            <span style="font-size: 8.5px; font-weight: 700; color: #4338ca; background-color: #e0e7ff; padding: 2px 5px; border-radius: 4px; letter-spacing: 0.5px; margin-left: 6px; display: inline-block;">AX PLATFORM</span>
          </td>
        </tr>
      </table>
      <!-- Name & Title -->
      <table cellpadding="0" cellspacing="0" border="0" style="width: 100%; margin-bottom: 6px;">
        <tr>
          <td style="font-size: 13.5px; font-weight: 800; color: #0f172a; line-height: 16px;">
            {{Name}} <span style="font-size: 10.5px; font-weight: 600; color: #64748b; margin-left: 4px;">{{JobTitle}} · {{Department}}</span>
          </td>
        </tr>
      </table>
      <!-- Divider -->
      <table cellpadding="0" cellspacing="0" border="0" style="width: 100%; margin-bottom: 6px;">
        <tr><td style="border-top: 1px solid #f1f5f9; height: 1px; font-size: 1px; line-height: 1px;">&nbsp;</td></tr>
      </table>
      <!-- Info Rows -->
      <table cellpadding="0" cellspacing="0" border="0" style="width: 100%; font-size: 10.5px; color: #475569; line-height: 14px;">
        <tr>
          <td style="width: 44px; color: #64748b; font-weight: 600; padding: 1.5px 0; vertical-align: middle;">📞 Tel</td>
          <td style="color: #1e293b; font-weight: 500; padding: 1.5px 0; vertical-align: middle;">{{Phone}}</td>
        </tr>
        <tr>
          <td style="width: 44px; color: #64748b; font-weight: 600; padding: 1.5px 0; vertical-align: middle;">✉️ Email</td>
          <td style="padding: 1.5px 0; vertical-align: middle;">
            <a href="mailto:{{Email}}" style="color: #4f46e5; text-decoration: none; font-weight: 600;">{{Email}}</a>
          </td>
        </tr>
        <tr>
          <td style="width: 44px; color: #64748b; font-weight: 600; padding: 1.5px 0; vertical-align: middle;">🌐 Web</td>
          <td style="padding: 1.5px 0; vertical-align: middle;">
            <a href="https://www.clouflake.com" target="_blank" rel="noopener noreferrer" style="color: #4f46e5; text-decoration: none; font-weight: 600;">https://www.clouflake.com</a>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;

/**
 * Generates an ultra-crisp PNG business card image (2x Retina scale)
 * Compact width (308px) matching the design sample exactly.
 * @param {Object} user User profile object
 * @returns {string} base64 DataURL (image/png)
 */
export const generateCardImageBase64 = (user = {}) => {
  if (typeof document === 'undefined') return '';

  const canvas = document.createElement('canvas');
  const scale = 2; // 2x Retina
  const width = 308;
  const height = 145;

  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  ctx.scale(scale, scale);

  // 1. Background Card with Rounded Corners
  const radius = 12;
  ctx.beginPath();
  ctx.moveTo(radius, 0);
  ctx.lineTo(width - radius, 0);
  ctx.quadraticCurveTo(width, 0, width, radius);
  ctx.lineTo(width, height - radius);
  ctx.quadraticCurveTo(width, height, width - radius, height);
  ctx.lineTo(radius, height);
  ctx.quadraticCurveTo(0, height, 0, height - radius);
  ctx.lineTo(0, radius);
  ctx.quadraticCurveTo(0, 0, radius, 0);
  ctx.closePath();

  ctx.fillStyle = '#ffffff';
  ctx.fill();

  // Subtle Border
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Left Indigo Accent Bar
  ctx.save();
  ctx.clip();
  ctx.fillStyle = '#4f46e5';
  ctx.fillRect(0, 0, 4.5, height);
  ctx.restore();

  // 2. Company Brand & Badge
  ctx.fillStyle = '#0f172a';
  ctx.font = 'bold 14px -apple-system, BlinkMacSystemFont, "Pretendard", "Segoe UI", Roboto, sans-serif';
  ctx.fillText('Clouflake', 16, 24);

  const brandWidth = ctx.measureText('Clouflake').width;

  // Badge Background
  ctx.fillStyle = '#e0e7ff';
  const badgeX = 16 + brandWidth + 7;
  const badgeY = 13;
  const badgeW = 70;
  const badgeH = 15;
  const bRadius = 4;
  ctx.beginPath();
  if (ctx.roundRect) {
    ctx.roundRect(badgeX, badgeY, badgeW, badgeH, bRadius);
  } else {
    ctx.rect(badgeX, badgeY, badgeW, badgeH);
  }
  ctx.fill();

  // Badge Text
  ctx.fillStyle = '#4338ca';
  ctx.font = 'bold 8.5px -apple-system, BlinkMacSystemFont, "Pretendard", sans-serif';
  ctx.fillText('AX PLATFORM', badgeX + 5.5, badgeY + 11);

  // 3. Name & Position
  const name = user.Name || '사용자';
  const jobTitle = user.JobTitle || user.Position || '';
  const department = user.Department || '';
  const subTitle = [jobTitle, department].filter(Boolean).join(' · ');

  ctx.fillStyle = '#0f172a';
  ctx.font = 'bold 13.5px -apple-system, BlinkMacSystemFont, "Pretendard", sans-serif';
  ctx.fillText(name, 16, 48);

  if (subTitle) {
    const nameWidth = ctx.measureText(name).width;
    ctx.fillStyle = '#64748b';
    ctx.font = '600 10.5px -apple-system, BlinkMacSystemFont, "Pretendard", sans-serif';
    ctx.fillText(subTitle, 16 + nameWidth + 5, 48);
  }

  // 4. Subtle Divider
  ctx.strokeStyle = '#f1f5f9';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(16, 58);
  ctx.lineTo(width - 16, 58);
  ctx.stroke();

  // 5. Info Rows (Tel, Email, Web)
  const phone = user.Phone || '02-123-4567';
  const displayEmail = (user.Aliases && user.Aliases.trim()) 
    ? user.Aliases.split(',')[0].trim() 
    : (user.Email || 'contact@clouflake.com');
  const web = 'https://www.clouflake.com';

  const drawRow = (icon, label, value, y, isLink = false) => {
    ctx.fillStyle = '#64748b';
    ctx.font = 'bold 10px -apple-system, BlinkMacSystemFont, "Pretendard", sans-serif';
    ctx.fillText(`${icon} ${label}`, 16, y);

    ctx.fillStyle = isLink ? '#4f46e5' : '#1e293b';
    ctx.font = isLink 
      ? '600 10.5px -apple-system, BlinkMacSystemFont, "Pretendard", sans-serif' 
      : '500 10.5px -apple-system, BlinkMacSystemFont, "Pretendard", sans-serif';
    ctx.fillText(value, 72, y);
  };

  drawRow('📞', 'Tel', phone, 78);
  drawRow('✉️', 'Email', displayEmail, 98, true);
  drawRow('🌐', 'Web', web, 118, true);

  return canvas.toDataURL('image/png');
};

/**
 * Returns HTML wrapped image for email body
 */
export const generateCardImageHtml = (user = {}) => {
  const dataUrl = generateCardImageBase64(user);
  if (!dataUrl) return '';

  return `<table cellpadding="0" cellspacing="0" border="0" style="margin-top: 14px; margin-bottom: 6px; border-collapse: collapse;">
  <tr>
    <td>
      <a href="https://www.clouflake.com" target="_blank" rel="noopener noreferrer" style="display: inline-block; text-decoration: none;">
        <img src="${dataUrl}" alt="${user.Name || 'Clouflake'} 명함" width="308" style="display: block; width: 308px; max-width: 100%; height: auto; border: 0; border-radius: 12px;" />
      </a>
    </td>
  </tr>
</table>`;
};
